/**
 * Owner rule, 2026-09-25: a customer sees only an error CODE and a REFERENCE (the short job
 * id an admin can search) — never a vendor's or the server's technical text. Admins keep the
 * full text; the full detail always stays in the log and the admin views.
 *
 * The cloud sends a customer the CODE itself in every job field (`app/ai_relay/error_codes.py`
 * is the table). These rules mirror it only for a failure that never reached a job row — a
 * refused request. Order matters: the first matching rule wins.
 */
export type ErrorCode =
  | "GEN-CREDITS" | "GEN-REFUSED" | "GEN-HELD" | "GEN-SETUP" | "GEN-LIMIT" | "GEN-INPUT"
  | "GEN-FRAMING" | "GEN-COVERAGE" | "GEN-NORMALIZE" | "GEN-REVIEW" | "GEN-UNKNOWN";

const CODES: [ErrorCode, RegExp][] = [
  // The owner's automatic checks (docs/higgsfield-generation-rules.md) — first: their named
  // reasons carry words ("perspective", "cropped") the broad rules below would also match.
  ["GEN-FRAMING", /framing_check_failed|case_check_failed|image_undecodable|clear_restore_skipped/i],
  ["GEN-COVERAGE", /coverage_check_failed|autokey_not_found|off-coverage|not-found/i],
  // A clear-image decision on a job that moved, or on an image that is no longer the stored one.
  ["GEN-REVIEW", /clear_review_stale/i],
  ["GEN-CREDITS", /insufficient_credits|out of credits|not enough credits|\b402\b/i],
  ["GEN-REFUSED", /moderat|nsfw|higgsfield_failed|higgsfield_canceled|content check|refused this image/i],
  ["GEN-HELD", /outcome.unknown|submit_unknown|timeout|timed out|did not finish|held/i],
  ["GEN-SETUP", /not_connected|not set|no_public_input_url|generation_disabled|hosting_public_host|unconfigured/i],
  ["GEN-LIMIT", /quota|daily_limit|daily limit|rate-limited|\b429\b|over_ceiling|no_quote|in.flight/i],
  ["GEN-INPUT", /brand_and_model_required|invalid|too large|must be|is empty|not an image|guardrail|blocked|not_allowed|prompt_unavailable/i],
];

const SERVER_CODE = /^GEN-[A-Z]+$/;

/** The server's own `detail` of a refused request, else the error's message. */
export function errorDetail(e: unknown): string {
  const detail = (e as { body?: { detail?: unknown } } | null)?.body?.detail;
  if (typeof detail === "string") return detail;
  return e instanceof Error ? e.message : String(e);
}

export function errorCode(detail: string | null | undefined): ErrorCode {
  if (detail && SERVER_CODE.test(detail)) return detail as ErrorCode;
  return CODES.find(([, rule]) => rule.test(detail ?? ""))?.[0] ?? "GEN-UNKNOWN";
}

export function codeAndReference(detail: string | null | undefined, reference?: string | null): string {
  const ref = reference ? ` · Reference ${reference.slice(0, 8)}` : "";
  return `Code ${errorCode(detail)}${ref}.`;
}

export function customerErrorText(detail: string | null | undefined, reference?: string | null): string {
  return `Generation could not finish. ${codeAndReference(detail, reference)}`;
}

/** A3 — what a customer reads for an automatic check's note, by its code. */
const CHECK_WORDS: Partial<Record<ErrorCode, string>> = {
  "GEN-FRAMING": "the case may not be framed as asked",
  "GEN-COVERAGE": "the print area may be the wrong size",
};

/** An automatic check's note: the full reason for an admin, plain words and the code otherwise. */
export function checkNoteText(
  note: string,
  { admin, reference }: { admin: boolean; reference?: string | null },
): string {
  if (admin) return note;
  const words = CHECK_WORDS[errorCode(note)] ?? "this image may need a second look";
  return `Automatic check: ${words}. ${codeAndReference(note, reference)}`;
}

/** What a failure reads as on screen: the full text for an admin, the code for everyone else. */
export function shownError(
  detail: string | null | undefined,
  { admin, reference }: { admin: boolean; reference?: string | null },
): string {
  return admin ? (detail ?? "") : customerErrorText(detail, reference);
}
